import React, { useState, useRef, useCallback, useEffect } from 'react';
import { callVarnakramaAPI, callVarnasaaraAPI } from '../services/VarnakramaService';
import { useSettings } from '../contexts/SettingsContext';
import VarnasaaraVisualization from './VarnasaaraVisualization';
import SanskritKeyboard from './SanskritKeyboard';
import {
  VarnakramaResponse,
  VarnasaaraData,
  VedaType,
  OutputType,
  VedaOption,
  OutputOption,
} from '../types';
import '../styles/Varnakrama.css';

// Svara characters for keyboard shortcuts (Ctrl+1, Ctrl+2, Ctrl+3)
const SVARA_CHARS = ['\ue302', '\ue301', '\ue303']; // svarita, anudatta, deergha svarita

const vedaOptions: VedaOption[] = [
  { label: 'Yajurveda (यजुर्वेद)', value: 'yajur' },
  { label: 'Rigveda (ऋग्वेद)', value: 'rig' },
  { label: 'Samaveda (सामवेद)', value: 'sama' },
  { label: 'Atharvaveda (अथर्ववेद)', value: 'atharva' },
];

const outputOptions: OutputOption[] = [
  { label: 'शुद्ध-वर्णक्रमः (Śuddha)', value: 'shuddha' },
  { label: 'स्वर-वर्णक्रमः (Svara)', value: 'svara' },
  { label: 'मात्रा-वर्णक्रमः (Mātrā)', value: 'maatra' },
  { label: 'अङ्ग-वर्णक्रमः (Aṅga)', value: 'anga' },
  { label: 'वर्णसार-वर्णक्रमः (Varṇasāra)', value: 'varnasaara' },
  { label: 'सर्वम् (All)', value: 'sarve' },
];

const Varnakrama: React.FC = () => {
  const [inputWord, setInputWord] = useState('');
  const [veda, setVeda] = useState<VedaType>('yajur');
  const [outputType, setOutputType] = useState<OutputType>('sarve');
  const [result, setResult] = useState<VarnakramaResponse | null>(null);
  const [varnasaaraData, setVarnasaaraData] = useState<VarnasaaraData | null>(null);
  const [loading, setLoading] = useState(false);
  const [varnasaaraLoading, setVarnasaaraLoading] = useState(false);
  const [varnasaaraError, setVarnasaaraError] = useState<string | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const { fontSizeLevel, setFontSizeLevel, scaledFontSize, minLevel, maxLevel } = useSettings();

  // Restore cursor position and focus after state update
  useEffect(() => {
    if (cursorPositionRef.current !== null && textareaRef.current) {
      const pos = cursorPositionRef.current;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
      cursorPositionRef.current = null;
    }
  }, [inputWord]);

  const handleKeyboardKeyPress = useCallback((key: string) => {
    const textarea = textareaRef.current;
    const selectionStart = textarea?.selectionStart ?? 0;
    const selectionEnd = textarea?.selectionEnd ?? 0;

    if (key === 'backspace') {
      if (selectionStart !== selectionEnd) {
        // Delete selected text
        setInputWord(prev => prev.slice(0, selectionStart) + prev.slice(selectionEnd));
        cursorPositionRef.current = selectionStart;
      } else if (selectionStart > 0) {
        // Delete character before cursor
        setInputWord(prev => prev.slice(0, selectionStart - 1) + prev.slice(selectionStart));
        cursorPositionRef.current = selectionStart - 1;
      }
    } else {
      // Insert key at cursor position (replacing any selected text)
      setInputWord(prev => prev.slice(0, selectionStart) + key + prev.slice(selectionEnd));
      cursorPositionRef.current = selectionStart + key.length;
    }
    setVarnasaaraData(null);
    setVarnasaaraError(null);
  }, []);

  const handleAnalyze = async () => {
    if (!inputWord.trim()) {
      return;
    }

    setLoading(true);
    setVarnasaaraLoading(true);
    setVarnasaaraError(null);

    try {
      const response = await callVarnakramaAPI(inputWord, veda, outputType);
      setResult(response);

      try {
        const varnasaaraResponse = await callVarnasaaraAPI(inputWord, veda);
        setVarnasaaraData(varnasaaraResponse);
      } catch (varnasaaraErr) {
        console.error('Varnasaara loading failed:', varnasaaraErr);
        setVarnasaaraError('वर्णसार विश्लेषणे त्रुटिः।');
      }

      // Scroll to results after a brief delay to let the DOM update
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to analyze. Please try again.');
    } finally {
      setLoading(false);
      setVarnasaaraLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputWord(e.target.value);
    setVarnasaaraData(null);
    setVarnasaaraError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAnalyze();
    }
    // Ctrl+1, Ctrl+2, Ctrl+3 for svara shortcuts
    if (e.ctrlKey && ['1', '2', '3'].includes(e.key)) {
      e.preventDefault();
      const svaraIndex = parseInt(e.key) - 1;
      handleKeyboardKeyPress(SVARA_CHARS[svaraIndex]);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFontSizeLevel(parseInt(e.target.value, 10));
  };

  const renderResults = () => {
    if (!result) {
      return null;
    }

    const sections = [
      { key: 'shuddha', title: 'शुद्ध-वर्णक्रमः', content: result.shuddha },
      { key: 'svara', title: 'स्वर-वर्णक्रमः', content: result.svara },
      { key: 'maatra', title: 'मात्रा-वर्णक्रमः', content: result.maatra },
      { key: 'anga', title: 'अङ्ग-वर्णक्रमः', content: result.anga },
    ];

    return (
      <div className="results-container">
        {sections.map(
          section =>
            section.content && (
              <div key={section.key} className="section-container">
                <h3 className="section-title" style={{ fontSize: scaledFontSize(18) }}>{section.title}</h3>
                <div className="word-display">
                  <p className="word-text" style={{ fontSize: scaledFontSize(22) }}>
                    {inputWord}
                  </p>
                </div>
                <div className="section-content">
                  <p
                    className="content-text"
                    style={{
                      fontSize: scaledFontSize(17),
                      lineHeight: `${scaledFontSize(26)}px`,
                    }}
                  >
                    {section.content}
                  </p>
                </div>
              </div>
            )
        )}
      </div>
    );
  };

  return (
    <div className="varnakrama-container" ref={containerRef}>
      {/* Header */}
      <header className="varnakrama-header">
        <a
          href="https://dharmaposhanam.in"
          target="_blank"
          rel="noopener noreferrer"
          className="header-logo-link"
        >
          <img
            src="/dharmaposhanam-logo.jpg"
            alt="Dharmaposhanam"
            className="header-logo"
          />
        </a>
        <a href="/about.html" className="header-about-link">
          About
        </a>
        <h1 className="varnakrama-title">वर्णक्रम दर्शिका</h1>
        <p className="varnakrama-subtitle">वर्णजातं निवेशय</p>

        {/* Font Size Slider */}
        <div className="font-size-slider-container">
          <span className="font-size-label">अ</span>
          <input
            type="range"
            min={minLevel}
            max={maxLevel}
            value={fontSizeLevel}
            onChange={handleSliderChange}
            className="font-size-slider"
            aria-label="Font size"
          />
          <span className="font-size-label font-size-label-large">अ</span>
        </div>
      </header>

      {/* Input Section */}
      <section className="input-section">
        <div className="text-input-container">
          <textarea
            ref={textareaRef}
            className="sanskrit-input"
            value={inputWord}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter Sanskrit text in Devanagari..."
            aria-label="Sanskrit text input"
            style={{ fontSize: scaledFontSize(18) }}
          />
          {!showKeyboard && (
            <button
              className="keyboard-icon-button"
              onClick={() => setShowKeyboard(true)}
              aria-label="Show keyboard"
              title="Show Sanskrit keyboard"
            >
              ⌨
            </button>
          )}
        </div>

        {/* Sanskrit Keyboard */}
        <SanskritKeyboard
          visible={showKeyboard}
          onKeyPress={handleKeyboardKeyPress}
          onEnter={handleAnalyze}
          onClose={() => setShowKeyboard(false)}
          scaledFontSize={scaledFontSize}
        />

        <div className="controls-container">
          <div className="picker-container">
            <label className="picker-label" htmlFor="veda-select" style={{ fontSize: scaledFontSize(16) }}>
              वेदः
            </label>
            <select
              id="veda-select"
              className="picker-select"
              value={veda}
              onChange={(e) => setVeda(e.target.value as VedaType)}
              style={{ fontSize: scaledFontSize(16) }}
            >
              {vedaOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="picker-container">
            <label className="picker-label" htmlFor="output-select" style={{ fontSize: scaledFontSize(16) }}>
              वर्णक्रम-प्रकारः
            </label>
            <select
              id="output-select"
              className="picker-select"
              value={outputType}
              onChange={(e) => setOutputType(e.target.value as OutputType)}
              style={{ fontSize: scaledFontSize(16) }}
            >
              {outputOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className="analyze-button"
          onClick={handleAnalyze}
          disabled={loading || !inputWord.trim()}
          aria-label="Analyze Sanskrit text"
          style={{ fontSize: scaledFontSize(18) }}
        >
          {loading ? <span className="spinner"></span> : 'प्रदर्शय'}
        </button>
      </section>

      {/* Results */}
      <div ref={resultsRef}>
        {renderResults()}

        {/* Varnasaara Visualization */}
        <VarnasaaraVisualization
          data={varnasaaraData}
          loading={varnasaaraLoading}
          error={varnasaaraError}
        />
      </div>

      {/* Floating Button */}
      <button
        className="floating-button"
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        ↑
      </button>
    </div>
  );
};

export default Varnakrama;
